<script setup lang="ts">
import { ref, watch } from 'vue';
import type {
  CatalogueItemDetail,
  CatalogueItemGroup,
} from '@/models/Catalogue';
import gettext from '@/i18n/gettext';
import CatalogueItemDetails from '@/components/catalogue/CatalogueItemDetails.vue';
import CatalogueItemImages from '@/components/catalogue/CatalogueItemImages.vue';
import CatalogueItemFiles from '@/components/catalogue/CatalogueItemFiles.vue';
import CatalogueItemChildren from '@/components/catalogue/CatalogueItemChildren.vue';
import CatalogueItemChangelog from '@/components/catalogue/CatalogueItemChangelog.vue';

const { $gettext } = gettext;

const props = defineProps<{
  modelValue: boolean;
  item?: CatalogueItemDetail;
  groups: CatalogueItemGroup[];
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const tab = ref('details');

const handleClose = () => {
  emit('update:modelValue', false);
};

// Reset to details tab when dialog opens or when a different item is shown
watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      tab.value = 'details';
    }
  },
);
watch(
  () => props.item?.uuid,
  () => {
    if (props.modelValue) {
      tab.value = 'details';
    }
  },
);
</script>

<template>
  <v-dialog
    :model-value="props.modelValue"
    max-width="900px"
    scrollable
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card v-if="item" class="item-view-card">
      <v-card-title class="d-flex align-center">
        <span>{{ item.name }}</span>
        <v-chip
          v-if="
            item.version && (item.nextVersionUuid || item.previousVersionUuid)
          "
          size="small"
          color="info"
          class="ml-2"
        >
          v{{ item.version }}
        </v-chip>
        <v-spacer />
        <v-btn icon="mdi-close" variant="text" @click="handleClose" />
      </v-card-title>
      <v-divider />
      <v-card-text>
        <v-tabs v-model="tab">
          <v-tab value="details">{{ $gettext('Details') }}</v-tab>
          <v-tab value="images">{{ $gettext('Images') }}</v-tab>
          <v-tab value="files">{{ $gettext('Files') }}</v-tab>
          <v-tab value="children">{{ $gettext('Components') }}</v-tab>
          <v-tab value="activity">{{ $gettext('Activity') }}</v-tab>
        </v-tabs>
        <v-window v-model="tab">
          <v-window-item value="details">
            <CatalogueItemDetails :item="item" :groups="groups" />
          </v-window-item>
          <v-window-item value="images">
            <CatalogueItemImages :item="item" :images="item.images || []" />
          </v-window-item>
          <v-window-item value="files">
            <CatalogueItemFiles :item="item" :files="item.files || []" />
          </v-window-item>
          <v-window-item value="children">
            <CatalogueItemChildren :item="item" :groups="groups" />
          </v-window-item>
          <v-window-item value="activity">
            <CatalogueItemChangelog
              :changelog="item.changelog || []"
              :groups="groups"
            />
          </v-window-item>
        </v-window>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.item-view-card {
  max-height: calc(100vh - 150px);
  display: flex;
  flex-direction: column;
}

.item-view-card :deep(.v-card-text) {
  overflow-y: auto;
}
</style>
